/**
 * Apprise product detection (shared contract rule): a selection / deployment
 * "is Apprise" when any item's product slug == "apprise" OR family == "apprise".
 */
import type { DeploymentRequest, Product } from '@/types'

export const APPRISE_SLUG = 'apprise'
export const APPRISE_FAMILY = 'apprise'
/** Only the Small SKU is seeded for Apprise. */
export const APPRISE_DEFAULT_SIZE = 'S' as const

/**
 * Shown wherever the Small profile is shown (Configure Sizes, the server section
 * and Review). The Small profile must never be described as sufficient or
 * supported for Apprise — nothing about Apprise's runtime is verified.
 */
export const APPRISE_UNVERIFIED_NOTICE =
  'Apprise runtime requirements (OS, memory, storage, ports) have not been verified. Confirm with the Apprise team before deploying beyond development.'

/** Exact text the application track reports in this scope (VM provisioning only). */
export const APPRISE_APP_NOT_CONFIGURED_MESSAGE =
  'Apprise application installer is not configured. No application has been installed.'

export function isAppriseProduct(product: Pick<Product, 'slug' | 'family'> | null | undefined): boolean {
  if (!product) return false
  return product.slug === APPRISE_SLUG || product.family === APPRISE_FAMILY
}

export function isAppriseDeployment(
  deployment: Pick<DeploymentRequest, 'items' | 'pipeline_config' | 'server'> | null | undefined,
): boolean {
  if (!deployment) return false
  if (deployment.pipeline_config?.apprise_vm || deployment.server) return true
  return (deployment.items ?? []).some(
    (i) => i.product_family === APPRISE_FAMILY || (i.product_name ?? '').trim().toLowerCase() === APPRISE_SLUG,
  )
}
